import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-hot-toast';
import { sendEtlDocumentToReportFeed } from '../../utils/api';
import { ToolbarButton } from '../ConfigPages';
import { EtlBanner } from '../EtlPages';

export default function SendDocumentToFeedModal({ document, onClose, onSent }) {
  const [fileName, setFileName] = useState(document.originalFileName || '');
  const [emailSubject, setEmailSubject] = useState(document.emailSubject || '');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const dialog = useRef(null);
  const submitting = useRef(false);

  useEffect(() => {
    const previousFocus = window.document.activeElement;
    dialog.current?.querySelector('input')?.focus();
    return () => previousFocus?.focus();
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setSending(true);
    setError(null);
    try {
      const result = await sendEtlDocumentToReportFeed(document.id, { fileName, emailSubject });
      if (!result.success) throw new Error(result.error || 'Could not send document');
      toast.success(`Queued for ${(result.feeds || []).map((feed) => feed.fileType).join(', ') || 'report feed processing'}. Track progress on the Status tab.`);
      onSent();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Could not send document');
    } finally {
      submitting.current = false;
      setSending(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Escape' && !sending) onClose();
    if (event.key !== 'Tab') return;
    const items = dialog.current.querySelectorAll('input:not(:disabled), button:not(:disabled)');
    const first = items[0], last = items[items.length - 1];
    if (!first) { event.preventDefault(); return; }
    if (event.shiftKey && window.document.activeElement === first) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && window.document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  };

  return createPortal(
    <div className="cp-modal-scrim">
      <div ref={dialog} className="cp-modal" role="dialog" aria-modal="true"
           aria-labelledby="send-document-title" onKeyDown={onKeyDown}>
        <header className="cp-modal-head"><h2 id="send-document-title">Send to report feed</h2></header>
        <form onSubmit={submit}>
          <div className="cp-modal-body">
            <p className="cp-code" style={{ marginBottom: 16 }}>
              Evaluate this document using the current feed rules for {document.locationName || `Hotel ${document.locationId}`}.
              Matching feeds will process it through ETL, including feeds that previously processed it.
            </p>
            <label className="block mb-4">
              Email subject
              <input className="cp-input" value={emailSubject} maxLength={2000} disabled={sending}
                     onChange={(event) => setEmailSubject(event.target.value)} />
            </label>
            {document.emailSubject == null && (
              <p className="cp-code" style={{ marginBottom: 16 }}>
                No original subject was saved. Enter it here if the feed matches by subject.
              </p>
            )}
            <label className="block mb-4">
              Filename
              <input className="cp-input" value={fileName} required maxLength={255} disabled={sending}
                     onChange={(event) => setFileName(event.target.value)} />
            </label>
            <p className="cp-code">Keep the file extension. The subject and filename above are used for this submission.</p>
            {error && <div style={{ marginTop: 16 }}><EtlBanner tone="error">{error}</EtlBanner></div>}
          </div>
          <footer className="cp-modal-foot">
            <ToolbarButton type="button" onClick={onClose} disabled={sending}>Cancel</ToolbarButton>
            <ToolbarButton type="submit" disabled={sending || !fileName.trim()}>
              {sending ? 'Sending…' : 'Send to report feed'}
            </ToolbarButton>
          </footer>
        </form>
      </div>
    </div>,
    window.document.body,
  );
}
